import { useEffect, useMemo, useState } from "react";
import { deviceApi } from "../../infrastructure/deviceApi";
import { diaLocal } from "../fecha";

export interface DailyEntry {
  date: string;
  total_mxn: number;
  total_sats: number;
  count: number;
}

export interface DaySummary {
  total_mxn: number;
  total_sats: number;
  count: number;
}

export interface TopProduct {
  name: string;
  quantity: number;
  total_mxn: number;
}

export interface DashboardSummary {
  today: DaySummary;
  last_7_days: DailyEntry[];
  top_products: TopProduct[];
}

const DIAS = 7;

/**
 * Resumen de la terminal, calculado en el cliente a partir de `/api/v2/sales`.
 *
 * En v1 esto lo agregaba el backend (`GET /dashboard/summary`). En v2 los reportes agregados
 * son del MANAGER (`/reports/summary`, JWT) y viven en /admin: una terminal no tiene ese
 * permiso. Como `/sales` ya devuelve las últimas ventas con sus líneas, el corte del día y
 * el ranking salen de ahí sin endpoint nuevo — y sin darle a la caja permisos de dueño.
 *
 * Límite conocido: son las últimas 200 ventas. Para un comercio de mucho volumen la ventana
 * de 7 días puede quedar incompleta; el reporte fiel es el del panel.
 */
export function useDashboard() {
  const [ventas, setVentas] = useState<Awaited<ReturnType<typeof deviceApi.sales.list>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    deviceApi.sales
      .list(200)
      .then((d) => {
        if (!cancelled) setVentas(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Error cargando el resumen");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const data = useMemo<DashboardSummary>(() => {
    // Sólo lo cobrado. Una orden `invoiced` o `expired` no es dinero que entró.
    const pagadas = ventas.filter((s) => s.status === "paid");

    const dias: DailyEntry[] = [];
    for (let i = DIAS - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = diaLocal(d);
      const delDia = pagadas.filter((s) => diaLocal(s.paid_at ?? s.created_at) === iso);
      dias.push({
        date: iso,
        total_mxn: delDia.reduce((acc, s) => acc + Number(s.total_mxn), 0),
        total_sats: delDia.reduce((acc, s) => acc + (s.amount_sats ?? 0), 0),
        count: delDia.length,
      });
    }

    const hoy = dias[dias.length - 1];

    // Ranking por producto del catálogo: las líneas libres (propina, ítem suelto) tienen
    // product_id null y quedan fuera. "Propina" en el top de productos sería absurdo.
    const porProducto = new Map<string, TopProduct>();
    const desde = dias[0].date;
    for (const s of pagadas) {
      if (diaLocal(s.paid_at ?? s.created_at) < desde) continue;
      for (const it of s.items ?? []) {
        if (!it.product_id) continue;
        const prev = porProducto.get(it.product_id) ?? {
          name: it.description,
          quantity: 0,
          total_mxn: 0,
        };
        prev.quantity += it.qty;
        prev.total_mxn += Number(it.line_total_mxn);
        porProducto.set(it.product_id, prev);
      }
    }
    const top_products = [...porProducto.values()]
      .sort((a, b) => b.total_mxn - a.total_mxn)
      .slice(0, 5);

    return {
      today: { total_mxn: hoy.total_mxn, total_sats: hoy.total_sats, count: hoy.count },
      last_7_days: dias,
      top_products,
    };
  }, [ventas]);

  return { data, loading, error };
}
